<script setup lang="ts">
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhConfirmButton,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhPanel,
    ZhSavePreview,
} from '@zerohuman/ui'
import { computed, onMounted, ref, useId, watch } from 'vue'
import { type AgentProvider, api } from '@/api'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { useUiMode } from '@/composables/useUiMode'
import { AGENT_PROVIDER_NAMES, AGENT_PROVIDERS, AGENT_SUBSCRIPTIONS } from '@/lib/agents'
import { required } from '@/lib/fieldRules'
import { buildPlanSignInPreview, buildProviderPreview } from '@/lib/formPreview'

type Provider = AgentProvider
/** OpenAI and Grok: a plan sign-in or an API key, on one form. Claude and OpenRouter keep their own. */
type PlanProvider = 'openai' | 'grok'

function isPlanProvider(provider: Provider | null): provider is PlanProvider {
    return provider === 'openai' || provider === 'grok'
}

/**
 * What each plan sign-in tells the person: whose tool the login is shared with, and what the provider hands back
 * to paste. OpenAI sends the browser to an address on the person's own computer where nothing answers: the page
 * does not load, and the address itself is what they paste back. xAI's consent page tries that address itself,
 * finds nothing there, and shows a code on the page, telling the person to paste it into Grok Build: that code is
 * what they paste back here.
 */
const PLAN_SIGN_IN: Record<PlanProvider, { sameAs: string; pasted: 'address' | 'code'; lands: string }> = {
    openai: { sameAs: 'Codex', pasted: 'address', lands: 'http://localhost:1455' },
    grok: { sameAs: 'Grok Build', pasted: 'code', lands: 'http://127.0.0.1' },
}

/** The paste step of a plan sign-in in the person's words: what to copy, and the field it goes in. */
function planPaste(provider: PlanProvider): { intro: string; label: string; help: string; example: string } {
    const { vendor } = AGENT_SUBSCRIPTIONS[provider]
    const { sameAs, pasted, lands } = PLAN_SIGN_IN[provider]
    if (pasted === 'address') {
        return {
            intro: `Once you have signed in, ${vendor} sends your browser to an address starting ${lands}. That page will not load, which is expected. Copy the whole address from the address bar and paste it here.`,
            label: 'Address you were sent to',
            help: "The whole address from your browser's address bar once you have signed in.",
            example: `${lands}/…`,
        }
    }
    return {
        intro: `Once you have signed in, ${vendor}'s page shows a code under "Enter this code to finish signing in". It says to paste the code into ${sameAs}: paste it here instead, whole.`,
        label: `Code ${vendor} showed you`,
        help: `The whole code on ${vendor}'s page once you have signed in.`,
        example: 'a long code with no spaces',
    }
}

const props = defineProps<{
    layer?: 'enterprise' | 'member'
    layerId?: string
    editorOnly?: boolean
    initialProvider?: Provider
    initialMode?: 'add' | 'edit'
    connectionId?: string
    availableProviders?: Provider[]
    /**
     * The editor around this form is locked (its owner is saving or removing something). The fieldset it sits in
     * already stops input; this says so on each field as well, so every field reads as disabled.
     */
    disabled?: boolean
}>()

const emit = defineEmits<{
    /** Which provider was saved, so the parent can find the connection it just created and name it. */
    saved: [provider: Provider, connectionId?: string]
    cancel: []
    'busy-change': [busy: boolean]
}>()

const PROVIDER_NAMES = AGENT_PROVIDER_NAMES
/** Names the provider Select's trigger; the form can be on a page more than once. */
const providerLabelId = useId()

const keyLayer = props.layer ?? 'enterprise'
const openrouterBound = ref(false)
const claudeBound = ref(false)
const claudeAuth = ref<'oauth' | 'api_key' | null>(null)
/** OpenAI and Grok at this layer: null when not bound, else whether it is a plan login or a key. */
const planAuth = ref<Record<PlanProvider, 'oauth' | 'api_key' | null>>({ openai: null, grok: null })
/** The key typed for OpenAI or Grok, whichever the form is on. */
const planKey = ref('')
const openrouterKey = ref('')
const claudeKey = ref('')
const oauthCode = ref('')
const oauthState = ref('')
/** The sign-in page a plan sign-in opened, kept so pressing Connect again reopens it rather than starting another. */
const authorizationUrl = ref('')
const busy = ref(false)
const error = ref<string | null>(null)
const saved = ref<string | null>(null)
const initialProviders = props.availableProviders ?? AGENT_PROVIDERS
/** Provider whose form is open, for a new agent or to replace an existing credential. */
const formProvider = ref<Provider | null>(initialFormProvider())
const editing = ref(props.editorOnly && props.initialMode === 'edit')

watch(busy, value => emit('busy-change', value), { flush: 'sync' })
watch(formProvider, clearCredentials)

const { mode: uiMode } = useUiMode()
/**
 * Each form's one field must not be blank (its `required`, and Save already waits for it), checked while typing
 * (#1757). One check per form, so saving one never flags another's field. The ids are this instance's own, as the
 * form can be on a page more than once.
 */
const fieldPrefix = useId()
const KEY_EMPTY = required('The key is empty.', 'Paste the API key from your provider.')
const claudeFields = useFieldValidation(
    computed(() => ({ claudeKey: claudeKey.value })),
    { claudeKey: required('The key is empty.', 'Paste a Console API key or the token from claude setup-token.') },
    { idPrefix: `${fieldPrefix}-claude` },
)
const planFields = useFieldValidation(
    computed(() => ({ planKey: planKey.value })),
    { planKey: KEY_EMPTY },
    { idPrefix: `${fieldPrefix}-plan` },
)
const openrouterFields = useFieldValidation(
    computed(() => ({ openrouterKey: openrouterKey.value })),
    { openrouterKey: KEY_EMPTY },
    { idPrefix: `${fieldPrefix}-openrouter` },
)
const codeFields = useFieldValidation(
    computed(() => ({ oauthCode: oauthCode.value })),
    { oauthCode: required('Nothing is pasted yet.', 'Paste the whole code or address the sign-in gave you.') },
    { idPrefix: `${fieldPrefix}-code` },
)

/** Which form's check a save of `provider`'s key runs. */
function keyFieldsOf(provider: Provider) {
    if (provider === 'claude') return claudeFields
    return isPlanProvider(provider) ? planFields : openrouterFields
}

/** A refused save in plain words; Advanced mode adds the code (#1749). */
function refusal(plain: string, caught: unknown): string {
    const code = caught instanceof Error ? caught.message : ''
    return uiMode.value === 'advanced' && code ? `${plain} (${code})` : plain
}

/** A provider is only ever one the picker offers; anything else set on its field leaves the form as it is. */
function chooseProvider(choice: unknown): void {
    formProvider.value = unboundProviders.value.find(provider => provider === choice) ?? formProvider.value
}

function initialFormProvider(): Provider | null {
    if (!props.editorOnly) return null
    if (props.initialProvider && (props.initialMode === 'edit' || initialProviders.includes(props.initialProvider))) {
        return props.initialProvider
    }
    return initialProviders[0] ?? null
}

const rows = computed(() => {
    const list: Array<{ provider: Provider; detail: string; note: string }> = []
    if (claudeBound.value) {
        list.push({
            provider: 'claude',
            detail: claudeAuth.value === 'oauth' ? 'Pro/Max seat' : 'Console API key',
            note: claudeAuth.value === 'oauth' ? 'Runs bill this subscription seat.' : 'Used before OpenRouter.',
        })
    }
    for (const provider of ['openai', 'grok'] as const) {
        const auth = planAuth.value[provider]
        if (auth) {
            list.push({
                provider,
                detail: auth === 'oauth' ? AGENT_SUBSCRIPTIONS[provider].seat : 'API key',
                note: auth === 'oauth' ? 'Runs bill this subscription seat.' : 'Used before OpenRouter.',
            })
        }
    }
    if (openrouterBound.value) {
        list.push({ provider: 'openrouter', detail: 'API key', note: 'Used when no other provider is bound.' })
    }
    return list
})

const unboundProviders = computed(() => {
    const providers = AGENT_PROVIDERS
    return props.editorOnly
        ? (props.availableProviders ?? providers)
        : providers.filter(provider => !rows.value.some(row => row.provider === provider))
})

const layerFilter = computed(() =>
    keyLayer === 'member' && props.layerId ? { layer: 'member' as const, layerId: props.layerId } : undefined,
)

onMounted(load)

async function load(): Promise<void> {
    if (props.editorOnly) return
    try {
        const status = await api.llmStatus(layerFilter.value)
        openrouterBound.value = status.providers?.openrouter ?? status.bound
        claudeBound.value = status.providers?.claude ?? false
        claudeAuth.value = status.providers?.claudeAuth ?? null
        planAuth.value = {
            openai: status.providers?.openai ? (status.providers.openaiAuth ?? 'api_key') : null,
            grok: status.providers?.grok ? (status.providers.grokAuth ?? 'api_key') : null,
        }
    } catch {
        error.value = 'Could not load agent keys.'
    }
}

function startAdd(): void {
    error.value = null
    saved.value = null
    editing.value = false
    formProvider.value = unboundProviders.value[0] ?? null
}

function startEdit(provider: Provider): void {
    error.value = null
    saved.value = null
    editing.value = true
    formProvider.value = provider
}

function closeForm(): void {
    formProvider.value = null
    editing.value = false
    clearCredentials()
}

function clearCredentials(): void {
    claudeKey.value = ''
    planKey.value = ''
    openrouterKey.value = ''
    oauthCode.value = ''
    oauthState.value = ''
    authorizationUrl.value = ''
    // Emptied by the form, not the person: no field shows an error until it is typed in or left again.
    for (const fields of [claudeFields, planFields, openrouterFields, codeFields]) fields.reset()
}

function bindingOptions(): { connectionId?: string; create?: boolean } | undefined {
    if (!props.editorOnly) return undefined
    if (props.initialMode === 'edit') {
        return props.connectionId ? { connectionId: props.connectionId } : undefined
    }
    return { create: true }
}

function cancelForm(): void {
    closeForm()
    emit('cancel')
}

async function save(provider: Provider): Promise<void> {
    if (busy.value) return
    if (keyFieldsOf(provider).check() !== null) return
    error.value = null
    saved.value = null
    busy.value = true
    let apiKey = openrouterKey.value
    if (provider === 'claude') apiKey = claudeKey.value
    else if (isPlanProvider(provider)) apiKey = planKey.value
    let didSave = false
    let resultConnectionId: string | undefined
    try {
        const options = bindingOptions()
        const result = options
            ? await api.bindLlm(apiKey, provider, keyLayer, props.layerId, options)
            : await api.bindLlm(apiKey, provider, keyLayer, props.layerId)
        resultConnectionId = result?.connectionId
        if (provider === 'claude') {
            claudeKey.value = ''
            saved.value = apiKey.trim().startsWith('sk-ant-oat')
                ? 'Claude subscription token saved. Runs bill this Pro/Max seat.'
                : 'Claude API key saved. Runs use Claude when this key is bound.'
        } else if (isPlanProvider(provider)) {
            planKey.value = ''
            saved.value = `${PROVIDER_NAMES[provider]} API key saved. Runs use ${PROVIDER_NAMES[provider]} when this key is bound.`
        } else {
            openrouterKey.value = ''
            saved.value = 'OpenRouter key saved. Used when no other provider is bound.'
        }
        closeForm()
        await load()
        didSave = true
    } catch (caught) {
        error.value = refusal('The key could not be saved. Check it and try again.', caught)
    } finally {
        busy.value = false
    }
    if (didSave) emit('saved', provider, resultConnectionId)
}

/** A refused finish of a plan sign-in in plain words: what was pasted and what to paste instead, where that is known. */
function finishRefusal(provider: PlanProvider, caught: unknown): string {
    const { vendor, plan } = AGENT_SUBSCRIPTIONS[provider]
    const code = caught instanceof Error ? caught.message : ''
    if (code === 'llm_oauth_code_missing') {
        return PLAN_SIGN_IN[provider].pasted === 'code'
            ? `That is the address of ${vendor}'s page, not the code. Once you have signed in, copy the code ${vendor} shows and paste that here.`
            : 'There is no code in that address. Copy the whole address your browser was sent to, which has code= in it, and paste that here.'
    }
    if (code.startsWith('llm_oauth_denied:')) {
        return `${vendor} did not allow the sign-in (${code.slice('llm_oauth_denied:'.length)}).`
    }
    if (code.startsWith('llm_oauth_state')) {
        return `That sign-in can no longer be completed: it expired, or it is not the one started here. Press Connect ${plan} to start again.`
    }
    const what = `Could not finish ${vendor} sign-in`
    return code ? `${what} (${code}).` : `${what}.`
}

/** The form's primary button, and so the preview's confirm: OpenRouter's says what it saves. */
const primaryLabel = computed(() => (formProvider.value === 'openrouter' ? 'Save key' : 'Save'))

/**
 * The save preview (#1756): pressing Save names the provider in plain words, never the key, and nothing is saved
 * until that is confirmed. Choosing another provider or changing the key hides it as stale.
 */
const preview = ref<string[] | null>(null)

/**
 * A plan sign-in's preview (#1756): on the add form, Connect first says in plain words what will be connected, and
 * the provider's sign-in opens only once that is confirmed, as Add tool does before its own OAuth sign-in. Signing in
 * again on an existing connection is an edit (CTO decision 1) and starts at once, as it always has.
 */
const signInFor = ref<'claude' | PlanProvider | null>(null)

const signInLabel = computed(() => {
    if (signInFor.value === 'claude') return 'Connect Claude Pro/Max'
    return signInFor.value ? `Connect ${AGENT_SUBSCRIPTIONS[signInFor.value].plan}` : ''
})

const signInPreview = computed(() =>
    signInFor.value
        ? buildPlanSignInPreview({
              planName: AGENT_SUBSCRIPTIONS[signInFor.value].plan,
              vendorName: AGENT_SUBSCRIPTIONS[signInFor.value].vendor,
          })
        : null,
)

watch([formProvider, claudeKey, planKey, openrouterKey], () => {
    preview.value = null
    signInFor.value = null
})

/** Validate first (CTO decision 19 on #1756): an empty key shows its error, takes focus and opens no preview. */
function openPreview(): void {
    if (!formProvider.value || keyFieldsOf(formProvider.value).check() !== null) return
    signInFor.value = null
    preview.value = buildProviderPreview({ providerName: PROVIDER_NAMES[formProvider.value] })
}

async function startSignIn(provider: 'claude' | PlanProvider): Promise<void> {
    if (provider === 'claude') await connectClaude()
    else await connectPlan(provider)
}

/** A plan sign-in is under way: its page was opened, and its answer has not been pasted back yet. */
const planSignInPending = computed(() => oauthState.value !== '' && authorizationUrl.value !== '')

/**
 * Connect on the form: adding asks first with the sign-in preview; signing in again on a connection, or reopening a
 * sign-in already under way, starts at once.
 */
function askSignIn(provider: 'claude' | PlanProvider): void {
    if (editing.value || planSignInPending.value) {
        void startSignIn(provider)
        return
    }
    preview.value = null
    signInFor.value = provider
}

async function confirmSignIn(): Promise<void> {
    const provider = signInFor.value
    if (!provider) return
    try {
        await startSignIn(provider)
    } finally {
        signInFor.value = null
    }
}

async function confirmPreview(): Promise<void> {
    const provider = formProvider.value
    preview.value = null
    if (provider) await save(provider)
}

/**
 * Save on the form: adding a provider opens the preview first; replacing the credential of a connection that already
 * exists is an edit, outside the form inventory (#1756, CTO decision 1), and saves at once as it always has.
 */
function submitForm(): void {
    if (editing.value && formProvider.value) {
        void save(formProvider.value)
        return
    }
    openPreview()
}

/** What happens once the key is gone — shown in the confirmation, so the choice is informed. */
const revokeImpact =
    keyLayer === 'member'
        ? 'This member’s runs fall back to the enterprise keys.'
        : 'Runs without their own key lose this provider.'

async function remove(provider: Provider): Promise<void> {
    error.value = null
    saved.value = null
    busy.value = true
    try {
        await api.unbindLlm(provider, keyLayer, props.layerId)
        if (formProvider.value === provider) {
            closeForm()
        }
        await load()
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not remove key (${caught.message}).` : 'Could not remove key.'
    } finally {
        busy.value = false
    }
}

async function connectClaude(): Promise<void> {
    if (busy.value) return
    error.value = null
    saved.value = null
    busy.value = true
    try {
        const options = bindingOptions()
        const started = options
            ? await api.startClaudeOAuth(keyLayer, props.layerId, options)
            : await api.startClaudeOAuth(keyLayer, props.layerId)
        oauthState.value = started.state
        window.open(started.authorizationUrl, '_blank', 'noopener,noreferrer')
    } catch (caught) {
        error.value =
            caught instanceof Error
                ? `Could not start Claude login (${caught.message}).`
                : 'Could not start Claude login.'
    } finally {
        busy.value = false
    }
}

async function finishClaudeOAuth(): Promise<void> {
    if (busy.value) return
    if (codeFields.check() !== null) return
    error.value = null
    saved.value = null
    busy.value = true
    let didSave = false
    let resultConnectionId: string | undefined
    try {
        const result = await api.completeClaudeOAuth(oauthCode.value, oauthState.value)
        resultConnectionId = result?.connectionId
        saved.value = 'Claude Pro/Max connected. Runs bill this subscription seat.'
        closeForm()
        await load()
        didSave = true
    } catch (caught) {
        error.value =
            caught instanceof Error
                ? `Could not finish Claude login (${caught.message}).`
                : 'Could not finish Claude login.'
    } finally {
        busy.value = false
    }
    if (didSave) emit('saved', 'claude', resultConnectionId)
}

/**
 * Opens the provider's sign-in for a ChatGPT or SuperGrok plan, as `connectClaude` does for a Claude one. A sign-in
 * already under way is reopened, not started again: a second start would be a new authorization with its own
 * state, and a code from the window opened first could then no longer be completed.
 */
async function connectPlan(provider: PlanProvider): Promise<void> {
    if (busy.value) return
    error.value = null
    saved.value = null
    if (planSignInPending.value) {
        window.open(authorizationUrl.value, '_blank', 'noopener,noreferrer')
        return
    }
    busy.value = true
    try {
        const started = await api.startSubscriptionOAuth(provider, keyLayer, props.layerId, bindingOptions())
        oauthState.value = started.state
        authorizationUrl.value = started.authorizationUrl
        window.open(started.authorizationUrl, '_blank', 'noopener,noreferrer')
    } catch (caught) {
        const what = `Could not start ${AGENT_SUBSCRIPTIONS[provider].vendor} sign-in`
        error.value = caught instanceof Error ? `${what} (${caught.message}).` : `${what}.`
    } finally {
        busy.value = false
    }
}

async function finishPlan(provider: PlanProvider): Promise<void> {
    if (busy.value) return
    if (codeFields.check() !== null) return
    error.value = null
    saved.value = null
    busy.value = true
    let didSave = false
    let resultConnectionId: string | undefined
    try {
        const result = await api.completeSubscriptionOAuth(provider, oauthCode.value, oauthState.value)
        resultConnectionId = result?.connectionId
        saved.value = `${AGENT_SUBSCRIPTIONS[provider].plan} connected. Runs bill this subscription seat.`
        closeForm()
        await load()
        didSave = true
    } catch (caught) {
        error.value = finishRefusal(provider, caught)
        if (caught instanceof Error && caught.message.startsWith('llm_oauth_state')) {
            // That sign-in can no longer be completed (it expired, or is not the one started here): Connect starts
            // a new one rather than reopening it.
            oauthCode.value = ''
            oauthState.value = ''
            authorizationUrl.value = ''
            codeFields.reset()
        }
    } finally {
        busy.value = false
    }
    if (didSave) emit('saved', provider, resultConnectionId)
}
</script>

<template>
	<div class="space-y-4">
		<p v-if="!editorOnly" class="text-muted-foreground text-sm">
			<template v-if="keyLayer === 'member'">
				This agent’s own model credentials. A personal subscription seat (Claude Pro/Max, ChatGPT, SuperGrok) bills
				that subscription, not the company API.
			</template>
			<template v-else>
				Enterprise keys for model calls. With more than one provider set, runs use <strong>Claude</strong> first, then
				OpenAI, then Grok, then OpenRouter. Personal subscription seats bind on the member.
			</template>
		</p>
		<p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
		<p v-if="saved" class="text-sm">{{ saved }}</p>
		<ul v-if="!editorOnly" class="space-y-2">
			<ZhPanel
				v-for="row in rows"
				:key="row.provider"
				as="li"
				class="flex items-start justify-between gap-3 text-sm"
			>
				<div>
					<p class="font-medium">{{ PROVIDER_NAMES[row.provider] }} · {{ row.detail }}</p>
					<p class="text-muted-foreground">{{ keyLayer }} · {{ row.note }}</p>
				</div>
				<div class="flex shrink-0 gap-2">
					<Button type="button" size="sm" variant="outline" :disabled="busy" @click="startEdit(row.provider)">
						Edit
					</Button>
					<ZhConfirmButton
						label="Delete"
						confirm-label="Revoke key"
						:title="`Revoke the ${PROVIDER_NAMES[row.provider]} key?`"
						:description="revokeImpact"
						:disabled="busy"
						@confirm="remove(row.provider)"
					/>
				</div>
			</ZhPanel>
		</ul>
		<p v-if="!editorOnly && !rows.length" class="text-muted-foreground text-sm">No agent keys bound at this layer yet.</p>
		<Button
			v-if="!editorOnly && !formProvider && unboundProviders.length"
			type="button"
			size="sm"
			variant="outline"
			@click="startAdd"
		>
			Add Agent
		</Button>
		<!--
			#1756: the key form is the shared form shell (a section of the page it sits on), with one help line on every
			field and a plain-words preview before the key is saved. A plan sign-in's code goes in a form of its own, under
			it, since one form cannot hold another. Inline, the fields sit on a kit panel of their own; in the connection
			editor they already sit in one, so the form draws none (`bare`), and neither does either preview it opens.
		-->
		<template v-if="formProvider">
		<ZhFormLayout
			:title="editing ? `Edit ${PROVIDER_NAMES[formProvider]}` : 'Add AI provider'"
			purpose="Saves the credential your agents use to reach this provider's AI models."
			heading="section"
			:bare="editorOnly"
			data-testid="agent-key-form"
			novalidate
			@submit.prevent="submitForm"
		>
			<template v-if="editing" #status>
				<p class="text-sm">
					Editing {{ PROVIDER_NAMES[formProvider] }}. A new credential replaces the current one.
				</p>
			</template>
			<!-- The word names the trigger; `aria-label` lands on the field the kit Select mirrors its choice on. -->
			<ZhFormField
				v-if="!editing"
				v-slot="{ controlId, helpId }"
				label="Provider"
				:label-id="providerLabelId"
				help="The company whose AI models your agents will use."
				example="Claude"
			>
				<Select
					:model-value="formProvider"
					:disabled="busy || disabled"
					aria-label="Provider"
					@update:model-value="chooseProvider"
				>
					<SelectTrigger :id="controlId" class="w-full" :aria-labelledby="providerLabelId" :aria-describedby="helpId">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="provider in unboundProviders" :key="provider" :value="provider">
							{{ PROVIDER_NAMES[provider] }}
						</SelectItem>
					</SelectContent>
				</Select>
			</ZhFormField>
			<template v-if="formProvider === 'claude'">
				<p class="text-muted-foreground text-xs">
					Connect Pro/Max (same login as Claude Code) or paste a Console API key /
					<code>claude setup-token</code>.
				</p>
				<div class="flex flex-wrap gap-2">
					<Button type="button" size="sm" :disabled="busy" @click="askSignIn('claude')">Connect Claude Pro/Max</Button>
				</div>
				<!-- #1757's check (CTO decisions 18 and 20 on #1756): a fixed id, described by the error, then the help line. -->
				<ZhFormField
					:id="claudeFields.fieldId('claudeKey')"
					:error-id="claudeFields.message('claudeKey').id"
					label="API key or setup-token"
					help="A Console API key, or the token claude setup-token prints."
					example="the key from your Anthropic Console"
				>
					<template #default="{ controlId, describedBy }">
						<Input
							v-model="claudeKey"
							:disabled="busy || disabled"
							type="password"
							autocomplete="off"
							required
							v-bind="claudeFields.bind('claudeKey')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="claudeFields.message('claudeKey')" />
					</template>
				</ZhFormField>
			</template>
			<template v-else-if="isPlanProvider(formProvider)">
				<p class="text-muted-foreground text-xs">
					Connect a {{ AGENT_SUBSCRIPTIONS[formProvider].plan }} (same login as
					{{ PLAN_SIGN_IN[formProvider].sameAs }}) or paste an {{ AGENT_SUBSCRIPTIONS[formProvider].vendor }} API key.
				</p>
				<div class="flex flex-wrap gap-2">
					<Button
						type="button"
						size="sm"
						:data-testid="`agent-plan-connect-${formProvider}`"
						:disabled="busy"
						@click="askSignIn(formProvider)"
					>
						{{ planSignInPending ? 'Open the sign-in again' : `Connect ${AGENT_SUBSCRIPTIONS[formProvider].plan}` }}
					</Button>
				</div>
				<ZhFormField
					:id="planFields.fieldId('planKey')"
					:error-id="planFields.message('planKey').id"
					label="API key"
					help="A key from the provider's developer console."
					example="the key from your API settings page"
				>
					<template #default="{ controlId, describedBy }">
						<Input
							v-model="planKey"
							:disabled="busy || disabled"
							type="password"
							autocomplete="off"
							required
							v-bind="planFields.bind('planKey')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="planFields.message('planKey')" />
					</template>
				</ZhFormField>
			</template>
			<template v-else>
				<p class="text-muted-foreground text-xs">Models only — GitHub and Slack stay on MCP Tools.</p>
				<ZhFormField
					:id="openrouterFields.fieldId('openrouterKey')"
					:error-id="openrouterFields.message('openrouterKey').id"
					label="API key"
					help="Your OpenRouter key; it reaches models only, never tools."
					example="the key from your OpenRouter settings"
				>
					<template #default="{ controlId, describedBy }">
						<Input
							v-model="openrouterKey"
							:disabled="busy || disabled"
							type="password"
							autocomplete="off"
							required
							v-bind="openrouterFields.bind('openrouterKey')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="openrouterFields.message('openrouterKey')" />
					</template>
				</ZhFormField>
			</template>
			<template #cancel>
				<Button type="button" size="sm" variant="outline" :disabled="busy" @click="cancelForm">Cancel</Button>
			</template>
			<template #primary>
				<!-- #1757: not held off while the key is empty, so pressing it shows the field's own message and fix. -->
				<Button type="submit" size="sm" :disabled="busy">{{ primaryLabel }}</Button>
			</template>
			<template #preview>
				<ZhSavePreview
					v-if="preview"
					:lines="preview"
					:confirm-label="primaryLabel"
					:pending="busy"
					:bare="editorOnly"
					@confirm="confirmPreview"
					@edit="preview = null"
				/>
				<ZhSavePreview
					v-else-if="signInPreview"
					:lines="signInPreview"
					:confirm-label="signInLabel"
					:pending="busy"
					:bare="editorOnly"
					@confirm="confirmSignIn"
					@edit="signInFor = null"
				/>
			</template>
		</ZhFormLayout>
		<!--
			#1757: the forms are `novalidate` and their buttons are not held off while a field is empty, so pressing one
			shows the field's own message and fix instead of a greyed-out button with no reason.
		-->
		<template v-if="formProvider === 'claude'">
				<form v-if="oauthState" class="space-y-2" novalidate @submit.prevent="finishClaudeOAuth">
					<p class="text-muted-foreground text-xs">
						Anthropic opens a page with an authorization code. Paste the <strong>entire</strong> string,
						including the part after <code>#</code>.
					</p>
					<div class="flex flex-wrap items-end gap-2">
						<ZhFormField
							:id="codeFields.fieldId('oauthCode')"
							:error-id="codeFields.message('oauthCode').id"
							label="Authorization code"
							help="The whole code Anthropic shows once you have signed in."
							example="a long code with a # part"
						>
							<template #default="{ controlId, describedBy }">
								<Input
									v-model="oauthCode"
									type="password"
									:disabled="busy || disabled"
									autocomplete="off"
									required
									class="min-w-48"
									v-bind="codeFields.bind('oauthCode')"
									:id="controlId"
									:aria-describedby="describedBy"
								/>
							</template>
							<template #error>
								<ZhFieldError v-bind="codeFields.message('oauthCode')" />
							</template>
						</ZhFormField>
						<Button type="submit" size="sm" :disabled="busy">Finish connect</Button>
					</div>
				</form>
			</template>
			<template v-else-if="isPlanProvider(formProvider)">
				<form v-if="oauthState" class="space-y-2" novalidate @submit.prevent="finishPlan(formProvider)">
					<p class="text-muted-foreground text-xs">{{ planPaste(formProvider).intro }}</p>
					<div class="flex flex-wrap items-end gap-2">
						<ZhFormField
							:id="codeFields.fieldId('oauthCode')"
							:error-id="codeFields.message('oauthCode').id"
							:label="planPaste(formProvider).label"
							:help="planPaste(formProvider).help"
							:example="planPaste(formProvider).example"
						>
							<template #default="{ controlId, describedBy }">
								<Input
									v-model="oauthCode"
									type="password"
									:disabled="busy || disabled"
									autocomplete="off"
									required
									class="min-w-48"
									v-bind="codeFields.bind('oauthCode')"
									:id="controlId"
									:aria-describedby="describedBy"
								/>
							</template>
							<template #error>
								<ZhFieldError v-bind="codeFields.message('oauthCode')" />
							</template>
						</ZhFormField>
						<Button type="submit" size="sm" :disabled="busy">Finish connect</Button>
					</div>
				</form>
			</template>
		</template>
	</div>
</template>
